import { useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../api/endpoints';
import { cancelRequest } from '../api/api';
import { useStore } from '../store/store';

export default function useCityZipAutocomplete({ enabled = false, initialQuery = '', limit = 10 }) {
  const { makeRequest: storeMakeRequest } = useStore();
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      setLoading(false);
      setError('');
      return;
    }

    if ((query || '').trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      setError('');
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // TODO: Consider moving the debounce/cancel to a shared hook for reuse
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        await storeMakeRequest({
          endpoint: API_ENDPOINTS.GEO_LOCATION_CITY_AUTOCOMPLETE,
          body: { query: query.trim(), limit },
          onSuccess: (data) => {
            const raw = Array.isArray(data?.results) ? data.results : [];

            const normalizeCity = (s = '') =>
              s
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, ' ');

            const normalized = raw
              .map((r) => {
                const rawPost = (r?.value || r?.postcode || r?.postalCode || '').toString().trim();
                const hasPostal = /^\d{5}$/.test(rawPost);
                const rawCity = (r?.city || r?.name || r?.value || '').toString().trim();
                const stateCode = (r?.stateCode || r?.state_code || '').toString().trim();
                const state = (r?.state || '').toString().trim();
                const type = hasPostal ? 'postal' : 'city';
                const value = hasPostal ? rawPost : rawCity;
                return {
                  type,
                  value,
                  city: rawCity,
                  stateCode: stateCode,
                  state: state,
                  label: r?.label || r?.formatted || undefined
                };
              })
              .filter((x) => x.value);

            // TODO: If we later differentiate same city by state, include stateCode in the key
            const seen = new Set();
            const unique = [];
            for (const r of normalized) {
              const key =
                r.type === 'city'
                  ? `city:${normalizeCity(r.value)}`
                  : `postal:${r.value}`;
              if (!seen.has(key)) {
                seen.add(key);
                unique.push(r);
              }
            }

            setSuggestions(unique);
          },
          onError: (errMsg) => {
            setError(errMsg || 'Failed to load suggestions');
            setSuggestions([]);
          },
          onFinally: () => setLoading(false)
        });
      } catch (err) {
        setError(typeof err === 'string' ? err : err?.message || 'Failed to load suggestions');
        setSuggestions([]);
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelRequest([API_ENDPOINTS.GEO_LOCATION_CITY_AUTOCOMPLETE]);
    };
  }, [enabled, query, limit]);

  const selectLabel = (item) => {
    if (!item) return '';
    if (item.type === 'postal') {
      const postcode = (item.value || '').trim();
      const city = (item.city || '').trim();
      const st = (item.stateCode || item.state || '').trim();
      const main = postcode ? `[${postcode}]` : '';
      const tail = city ? ` ${city}` : '';
      const region = st ? ` (${st})` : '';
      return `${main}${tail}${region}`.trim();
    }
    const city = (item.value || item.city || '').trim();
    const st = (item.stateCode || item.state || '').trim();
    return `${city}${st ? ` (${st})` : ''}`.trim();
  };
  const selectValue = (item) => item?.value || selectLabel(item);

  return useMemo(
    () => ({
      query,
      setQuery,
      suggestions,
      loading,
      error,
      selectLabel,
      selectValue,
      clear: () => setQuery('')
    }),
    [query, suggestions, loading, error]
  );
}
